import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

export default function AiImageDownloadDialog({ open, imageUrl, onClose, onUpscale, onDownloadOriginal }) {
  const [upscaling, setUpscaling] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [upscaledImage, setUpscaledImage] = useState(null);
  const [error, setError] = useState('');
  const [originalResolution, setOriginalResolution] = useState('');
  
  // Track open state in a mutable ref to prevent stale closures in setTimeout
  const isOpenRef = React.useRef(open);

  useEffect(() => {
    isOpenRef.current = open;
    if (open) {
      setUpscaledImage(null);
      setError('');
      setOriginalResolution('');

      if (imageUrl) {
        const img = new Image();
        img.onload = () => {
          const width = img.naturalWidth;
          if (width >= 7000) setOriginalResolution('8K');
          else if (width >= 3000) setOriginalResolution('4K');
          else if (width >= 1800) setOriginalResolution('2K');
          else if (width >= 1400) setOriginalResolution('1.5K');
          else if (width >= 900) setOriginalResolution('1K');
          else if (width > 0) setOriginalResolution(`${width}px`);
        };
        img.src = imageUrl;
      }
    }
  }, [open, imageUrl]);

  const upscale = async () => {
    if (upscaling || !onUpscale) return;
    setUpscaling(true);
    setError('');
    try {
      const { initiate, checkStatus } = onUpscale();
      const initialResult = await initiate();

      if (initialResult?.status === 'completed') {
        setUpscaledImage(initialResult);
        setUpscaling(false);
        return;
      }

      let attempts = 0;
      const maxAttempts = 100;

      const poll = async () => {
        try {
          if (!isOpenRef.current) {
            setUpscaling(false);
            return;
          }
          const result = await checkStatus();
          if (result.status === 'completed') {
            setUpscaledImage(result);
            setUpscaling(false);
          } else if (result.status === 'processing') {
            attempts += 1;
            if (attempts < maxAttempts) {
              setTimeout(poll, 3000);
            } else {
              setError('Upscale is taking longer than expected. Please check back later.');
              setUpscaling(false);
            }
          } else {
            setError('Upscale failed or was cancelled.');
            setUpscaling(false);
          }
        } catch (err) {
          setError(err.data?.message || err.message || 'Error checking upscale status.');
          setUpscaling(false);
        }
      };

      setTimeout(poll, 3000);
    } catch (err) {
      setError(err.data?.message || err.message || 'Unable to upscale this image. Please try again.');
      setUpscaling(false);
    }
  };

  const download = async (url) => {
    if (downloading) return;
    setDownloading(true);
    setError('');
    try {
      const downloadUrl = url || await onDownloadOriginal();
      if (downloadUrl) {
        window.location.assign(downloadUrl);
      } else {
        setError('Unable to create a download link. Please try again.');
      }
    } catch (err) {
      setError(err.data?.message || err.message || 'Unable to download this image.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={upscaling || downloading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Download AI Image</DialogTitle>
      <DialogContent>
        {imageUrl && (
          <Box display="flex" justifyContent="center" mb={2}>
            <img
              src={upscaledImage?.imageUrl || imageUrl}
              alt={upscaledImage ? 'Upscaled AI image' : 'AI image'}
              style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain', borderRadius: 8 }}
            />
          </Box>
        )}
        <Typography color="textSecondary">
          {upscaledImage
            ? 'Upscaling is complete. Download the enhanced image or the original.'
            : 'Download the original image or create an upscaled version first.'}
        </Typography>
        {upscaling && (
          <Box mt={2}>
            <LinearProgress />
          </Box>
        )}
        {error && <Typography color="error" style={{ marginTop: 12 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={upscaling || downloading}>Close</Button>
        <Button onClick={() => download()} disabled={upscaling || downloading}>
          {originalResolution ? `Download Original (${originalResolution})` : 'Download Original'}
        </Button>
        {upscaledImage && (
          <Button 
            onClick={() => download(upscaledImage.downloadUrl)} 
            color="primary" 
            variant="contained" 
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {downloading ? 'Downloading...' : 'Download upscaled image'}
          </Button>
        )}
        {!upscaledImage && (
          <Button onClick={upscale} color="primary" variant="contained" disabled={upscaling || !onUpscale}>
            {upscaling ? 'Upscaling…' : 'Upscale to 8K'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
